// React
import { useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
// Componentes externos
import { Button } from "react-bootstrap";
import { ColumnDef, Row } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquarePlus,
  faPen,
  faTrash,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
// Componentes
import CustomTable from "../../../components/CustomTable/CustomTable";
import { Loading } from "../../../components/Loading/Loading";
// Hooks
import { useMutationData } from "../../../hooks/useMutationData";
import { useGetData } from "../../../hooks/useGetData";
// Interfaces
import { UsuarioInterface } from "../../../interfaces/UsuarioInterface";
// Contexto del tema
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";
import { useBloquesContext } from "../../../context/BloquesContext";
import { AuthContext } from "../../../context/AuthContext";
import { BLOQUES } from "../../../constants/bloques";

export const UsuarioListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { usuario } = useContext(AuthContext);
  const { verificarBloqueActivo } = useBloquesContext();

  const mutateUsuario = useMutationData({
    url: "/usuario",
    reload: ["usuario"],
  });

  const queryUsuario = useGetData<UsuarioInterface[]>({
    url: "/usuario?includeRol[eq]=true",
    name: ["usuario"],
  });

  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<UsuarioInterface>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: (info) => info.getValue() as number,
        footer: (props) => props.column.id,
        enableColumnFilter: false,
      },
      {
        accessorKey: "correo",
        header: () => "Correo",
        cell: (info) => info.getValue() as string,
      },
      {
        accessorKey: "nombre",
        header: () => "Nombre",
        cell: (info) => info.getValue() as string,
      },
      {
        accessorKey: "apellido_paterno",
        header: () => "Apellido paterno",
        cell: (info) => info.getValue() as string,
      },
      {
        accessorKey: "apellido_materno",
        header: () => "Apellido materno",
        cell: (info) => info.getValue() as string,
      },
      {
        id: "rol",
        header: "Rol",
        accessorFn: (row) => row.rol.nombre,
        cell: (info) => info.getValue() as string,
      },
      {
        id: "estatus",
        header: "Estatus",
        accessorFn: (row) => (row.estatus === 1 ? "Activo" : "Inactivo"),
        cell: (info) => {
          const estatus = info.getValue() as string;
          const isActive = estatus === "Activo";

          return (
            <span
              className={`${
                isActive ? "text-green-700" : "text-red-700"
              } font-bold px-2 py-0.5 rounded-full text-sm bg-opacity-80 ${
                isActive ? "bg-green-200" : "bg-red-200"
              }`}
            >
              {estatus}
            </span>
          );
        },
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="flex space-x-2">
            {verificarBloqueActivo(BLOQUES.EDITAR_USUARIO) && (
              <Button
                title="Editar usuario"
                className={`${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-500 hover:bg-blue-600"
                } text-white`}
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/usuario/editar/${info.row.original.id}`);
                }}
              >
                <FontAwesomeIcon icon={faPen} className="text-white" />
              </Button>
            )}
            {info.row.original.estatus === 0 && (
              <Button
                title="Reactivar usuario"
                className={`${
                  isDarkMode
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-green-500 hover:bg-green-600"
                } text-white`}
                onClick={() =>
                  mutateUsuario.mutate({
                    id: info.row.original.id,
                    data: { estatus: 1 },
                    tipo: "patch",
                  })
                }
              >
                <FontAwesomeIcon icon={faRotateLeft} className="text-white" />
              </Button>
            )}
            {info.row.original.estatus === 1 && (
              <Button
                title="Eliminar usuario"
                className={`${
                  isDarkMode
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-500 hover:bg-red-600"
                } text-white`}
                onClick={() =>
                  mutateUsuario.mutate({
                    id: info.row.original.id,
                    data: { estatus: 0 },
                    tipo: "delete",
                  })
                }
              >
                <FontAwesomeIcon icon={faTrash} className="text-white" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, mutateUsuario, isDarkMode]
  );

  if (queryUsuario.isLoading) {
    return <Loading />;
  }

  // if (isBloquesEmpty) {
  //   return <div>No hay bloques disponibles</div>;
  // }

  return (
    <div
      className={`p-6 min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-3xl font-semibold">Usuarios</h2>
        {verificarBloqueActivo(BLOQUES.CREAR_USUARIO) && (
          <Button
            title="Crear usuario"
            onClick={() => {
              window.scrollTo(0, 0);
              navigate(`/usuario/crear`);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FontAwesomeIcon icon={faSquarePlus} className="mr-2 text-white" />{" "}
            Crear usuario
            
          </Button>
        )}
      </div>

      {queryUsuario.data && (
        <div
          className={`shadow-md rounded-lg p-4 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <CustomTable
            {...{
              data: queryUsuario.data,
              columns,
              getRowProps: (row: Row<UsuarioInterface>) => ({
                className:
                  row.original.estatus === 1 ? "bg-green-100" : "bg-red-100",
              }),
            }}
          />
        </div>
      )}
    </div>
  );
};
